import { useState, useRef, useCallback } from "react";
import {
  Upload,
  Link as LinkIcon,
  Trash2,
  Image as ImageIcon,
  FolderOpen,
  Loader2,
  Eye,
  Copy,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  useGetMediaQuery,
  useUploadMediaMutation,
  useImportMediaFromUrlMutation,
  useDeleteMediaMutation,
} from "@/store";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import type { Media, MediaFolder, ImageTransformOptions } from "@/types";

const FOLDERS: { value: MediaFolder | "all"; label: string }[] = [
  { value: "all", label: "All Media" },
  { value: "products", label: "Products" },
  { value: "categories", label: "Categories" },
  { value: "general", label: "General" },
];

export function MediaManagerScreen() {
  const [selectedFolder, setSelectedFolder] = useState<MediaFolder | "all">("all");
  const [page, setPage] = useState(1);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<Media | null>(null);
  const [deleteMedia, setDeleteMedia] = useState<Media | null>(null);
  const [uploadFolder, setUploadFolder] = useState<MediaFolder>("general");
  const [importUrl, setImportUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [transforms, setTransforms] = useState<ImageTransformOptions>({
    sharpen: false,
    upscale: false,
    enhance: false,
    removeBackground: false,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: mediaData, isLoading } = useGetMediaQuery({
    folder: selectedFolder === "all" ? undefined : selectedFolder,
    page,
    limit: 24,
  });

  const [uploadMediaMutation, { isLoading: isUploading }] = useUploadMediaMutation();
  const [importMediaMutation, { isLoading: isImporting }] = useImportMediaFromUrlMutation();
  const [deleteMediaMutation, { isLoading: isDeleting }] = useDeleteMediaMutation();

  const media = mediaData?.data || [];
  const pagination = mediaData?.pagination;

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload an image.");
      return;
    }

    try {
      await uploadMediaMutation({ 
        file, 
        folder: uploadFolder,
        transforms: transforms,
      }).unwrap();
      toast.success("Media uploaded successfully");
      setUploadDialogOpen(false);
      // Reset transforms after upload
      setTransforms({ sharpen: false, upscale: false, enhance: false });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: any) {
      const errorMessage = error?.data?.error || error?.data?.message || "Failed to upload media";
      toast.error(errorMessage);
    }
  };

  const handleImportFromUrl = async () => {
    if (!importUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    try {
      new URL(importUrl);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    try {
      await importMediaMutation({ 
        url: importUrl, 
        folder: uploadFolder,
        transforms: transforms,
      }).unwrap();
      toast.success("Media imported successfully");
      setImportDialogOpen(false);
      setImportUrl("");
      // Reset transforms after import
      setTransforms({ sharpen: false, upscale: false, enhance: false, removeBackground: false });
    } catch (error: any) {
      const errorMessage = error?.data?.error || error?.data?.message || "Failed to import media";
      toast.error(errorMessage);
    }
  };

  const handleDelete = async () => {
    if (!deleteMedia) return;

    try {
      await deleteMediaMutation(deleteMedia.id).unwrap();
      toast.success("Media deleted successfully");
      setDeleteMedia(null);
    } catch (error: any) {
      const errorMessage = error?.data?.error || error?.data?.message || "Failed to delete media";
      toast.error(errorMessage);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  }, [uploadFolder]);

  const copyToClipboard = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      toast.success("URL copied to clipboard");
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Failed to copy URL");
    }
  };

  const formatBytes = (bytes?: number) => {
    if (!bytes) return "Unknown size";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Media Manager</h1>
          <p className="text-muted-foreground">
            Upload and manage your media files
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setImportDialogOpen(true)}
            className="gap-2"
          >
            <LinkIcon className="h-4 w-4" />
            Import from URL
          </Button>
          <Button onClick={() => setUploadDialogOpen(true)} className="gap-2">
            <Upload className="h-4 w-4" />
            Upload
          </Button>
        </div>
      </div>

      {/* Folder Tabs */}
      <Tabs
        value={selectedFolder}
        onValueChange={(value) => {
          setSelectedFolder(value as MediaFolder | "all");
          setPage(1);
        }}
      >
        <TabsList>
          {FOLDERS.map((folder) => (
            <TabsTrigger key={folder.value} value={folder.value}>
              {folder.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Media Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            {FOLDERS.find((f) => f.value === selectedFolder)?.label}
          </CardTitle>
          <CardDescription>
            {pagination?.total || 0} {(pagination?.total || 0) === 1 ? "file" : "files"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : media.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No media found</p>
              <Button onClick={() => setUploadDialogOpen(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload Your First File
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {media.map((item) => (
                  <div
                    key={item.id}
                    className="group relative aspect-square rounded-lg border overflow-hidden bg-muted cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                    onClick={() => setPreviewMedia(item)}
                  >
                    <img
                      src={item.thumbnailUrl || item.url}
                      alt={item.filename}
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        size="icon"
                        variant="secondary"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewMedia(item);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="secondary"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(item.url, item.id);
                        }}
                      >
                        {copiedId === item.id ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        size="icon"
                        variant="destructive"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteMedia(item);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                      <p className="text-xs text-white truncate">{item.filename}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                    disabled={page === pagination.totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>Upload Media</DialogTitle>
            <DialogDescription>
              Upload an image file to your media library
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <div className="space-y-2">
              <Label htmlFor="folder">Folder</Label>
              <Select
                value={uploadFolder}
                onValueChange={(value) => setUploadFolder(value as MediaFolder)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="products">Products</SelectItem>
                  <SelectItem value="categories">Categories</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Image Transformations */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">AI Enhancements (Optional)</Label>
              <div className="rounded-lg border p-3 space-y-3 bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="enhance" className="text-sm font-medium cursor-pointer">
                      AI Enhance
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Automatically improve image quality
                    </p>
                  </div>
                  <Switch
                    id="enhance"
                    checked={transforms.enhance}
                    onCheckedChange={(checked) => 
                      setTransforms((prev) => ({ ...prev, enhance: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="upscale" className="text-sm font-medium cursor-pointer">
                      Upscale
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Increase resolution with AI
                    </p>
                  </div>
                  <Switch
                    id="upscale"
                    checked={transforms.upscale}
                    onCheckedChange={(checked) => 
                      setTransforms((prev) => ({ ...prev, upscale: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="sharpen" className="text-sm font-medium cursor-pointer">
                      Sharpen
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Make image edges crisper
                    </p>
                  </div>
                  <Switch
                    id="sharpen"
                    checked={transforms.sharpen}
                    onCheckedChange={(checked) => 
                      setTransforms((prev) => ({ ...prev, sharpen: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="remove-background" className="text-sm font-medium cursor-pointer">
                      Remove Background
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Remove background using AI
                    </p>
                  </div>
                  <Switch
                    id="remove-background"
                    checked={transforms.removeBackground}
                    onCheckedChange={(checked) => 
                      setTransforms((prev) => ({ ...prev, removeBackground: checked }))
                    }
                  />
                </div>
              </div>
            </div>

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-primary/50"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e.target.files)}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <Upload className="h-10 w-10 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Drag and drop or click to upload
                </span>
                <span className="text-xs text-muted-foreground">
                  PNG, JPG, GIF, WebP, SVG up to 10MB
                </span>
              </label>
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button
              variant="outline"
              onClick={() => setUploadDialogOpen(false)}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                "Select File"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from URL Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Import from URL</DialogTitle>
            <DialogDescription>
              Import an image from an external URL
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="folder">Folder</Label>
              <Select
                value={uploadFolder}
                onValueChange={(value) => setUploadFolder(value as MediaFolder)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="products">Products</SelectItem>
                  <SelectItem value="categories">Categories</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="url">Image URL</Label>
              <Input
                id="url"
                type="url"
                placeholder="https://example.com/image.jpg"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
              />
            </div>

            {/* Image Transformations */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">AI Enhancements (Optional)</Label>
              <div className="rounded-lg border p-3 space-y-3 bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="import-enhance" className="text-sm font-medium cursor-pointer">
                      AI Enhance
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Automatically improve image quality
                    </p>
                  </div>
                  <Switch
                    id="import-enhance"
                    checked={transforms.enhance}
                    onCheckedChange={(checked) => 
                      setTransforms((prev) => ({ ...prev, enhance: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="import-upscale" className="text-sm font-medium cursor-pointer">
                      Upscale
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Increase resolution with AI
                    </p>
                  </div>
                  <Switch
                    id="import-upscale"
                    checked={transforms.upscale}
                    onCheckedChange={(checked) => 
                      setTransforms((prev) => ({ ...prev, upscale: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="import-sharpen" className="text-sm font-medium cursor-pointer">
                      Sharpen
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Make image edges crisper
                    </p>
                  </div>
                  <Switch
                    id="import-sharpen"
                    checked={transforms.sharpen}
                    onCheckedChange={(checked) => 
                      setTransforms((prev) => ({ ...prev, sharpen: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="import-remove-background" className="text-sm font-medium cursor-pointer">
                      Remove Background
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Remove background using AI
                    </p>
                  </div>
                  <Switch
                    id="import-remove-background"
                    checked={transforms.removeBackground}
                    onCheckedChange={(checked) => 
                      setTransforms((prev) => ({ ...prev, removeBackground: checked }))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setImportDialogOpen(false);
                setImportUrl("");
                setTransforms({ sharpen: false, upscale: false, enhance: false, removeBackground: false });
              }}
              disabled={isImporting}
            >
              Cancel
            </Button>
            <Button onClick={handleImportFromUrl} disabled={isImporting}>
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                "Import"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewMedia} onOpenChange={() => setPreviewMedia(null)}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="truncate">{previewMedia?.filename}</DialogTitle>
            <DialogDescription>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge variant="outline">{previewMedia?.format.toUpperCase()}</Badge>
                {previewMedia?.width && previewMedia?.height && (
                  <Badge variant="outline">
                    {previewMedia.width} x {previewMedia.height}
                  </Badge>
                )}
                <Badge variant="outline">{formatBytes(previewMedia?.bytes)}</Badge>
                <Badge variant="secondary">{previewMedia?.folder}</Badge>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <img
              src={previewMedia?.url}
              alt={previewMedia?.filename}
              className="w-full max-h-[60vh] object-contain rounded-lg"
            />
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={previewMedia?.url || ""}
              readOnly
              className="flex-1 font-mono text-xs"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() =>
                previewMedia && copyToClipboard(previewMedia.url, previewMedia.id)
              }
            >
              {copiedId === previewMedia?.id ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={() => {
                setDeleteMedia(previewMedia);
                setPreviewMedia(null);
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button variant="outline" onClick={() => setPreviewMedia(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteMedia} onOpenChange={() => setDeleteMedia(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Media?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteMedia?.filename}" from your media
              library. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
